import React from "react";

interface UseDevicesReturn {
  videoDevices: MediaDeviceInfo[];
  audioDevices: MediaDeviceInfo[];
  videoTrack: MediaStreamTrack | undefined;
  audioTrack: MediaStreamTrack | undefined;
  selectedVideoDeviceId: string;
  selectedAudioDeviceId: string;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  devicesStatus: "IDLE" | "LOADING" | "READY" | "ERROR";
  initializeDevices: () => void;
  cleanupDevices: () => void;
  handleVideoChange: (deviceId: string) => void;
  handleAudioChange: (deviceId: string) => void;
  toggleVideo: () => void;
  toggleAudio: () => void;
}

export function useDevices(): UseDevicesReturn {
  const [shouldInitialize, setShouldInitialize] = React.useState(false);
  const [videoDevices, setVideoDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [videoTrack, setVideoTrack] = React.useState<MediaStreamTrack | undefined>();
  const [audioTrack, setAudioTrack] = React.useState<MediaStreamTrack | undefined>();
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = React.useState<string>("");
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = React.useState<string>("");
  const [isVideoEnabled, setIsVideoEnabled] = React.useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = React.useState(true);
  const [devicesStatus, setDevicesStatus] = React.useState<"IDLE" | "LOADING" | "READY" | "ERROR">("IDLE");
  const streamRef = React.useRef<MediaStream | null>(null);

  const initializeDevices = React.useCallback(() => {
    setShouldInitialize(true);
  }, []);

  const cleanupDevices = React.useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setVideoTrack(undefined);
    setAudioTrack(undefined);
    setIsVideoEnabled(true);
    setIsAudioEnabled(true);
    setSelectedVideoDeviceId("");
    setSelectedAudioDeviceId("");
    setShouldInitialize(false);
    setDevicesStatus("IDLE");
  }, []);

  const handleVideoChange = React.useCallback(
    async (deviceId: string) => {
      if (!streamRef.current) return;
      setDevicesStatus("LOADING");
      const video = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } });
      if (video.getVideoTracks().length === 0) {
        setDevicesStatus("ERROR");
        return;
      }
      const newTrack = video.getVideoTracks()[0];
      const oldTrack = videoTrack;
      if (oldTrack) {
        streamRef.current.removeTrack(oldTrack);
        oldTrack.stop();
      }
      streamRef.current.addTrack(newTrack);
      setVideoTrack(newTrack);
      setSelectedVideoDeviceId(deviceId);
      setDevicesStatus("READY");
    },
    [videoTrack],
  );

  const handleAudioChange = React.useCallback(
    async (deviceId: string) => {
      if (!streamRef.current) return;
      setDevicesStatus("LOADING");
      const audio = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } } });
      if (audio.getAudioTracks().length === 0) {
        setDevicesStatus("ERROR");
        return;
      }
      const newTrack = audio.getAudioTracks()[0];
      const oldTrack = audioTrack;
      if (oldTrack) {
        streamRef.current.removeTrack(oldTrack);
        oldTrack.stop();
      }
      streamRef.current.addTrack(newTrack);
      setAudioTrack(newTrack);
      setSelectedAudioDeviceId(deviceId);
      setDevicesStatus("READY");
    },
    [audioTrack],
  );

  const toggleVideo = React.useCallback(() => {
    if (videoTrack) {
      const enabled = !videoTrack.enabled;
      videoTrack.enabled = enabled;
      setIsVideoEnabled(enabled);
    }
  }, [videoTrack]);

  const toggleAudio = React.useCallback(() => {
    if (audioTrack) {
      const enabled = !audioTrack.enabled;
      audioTrack.enabled = enabled;
      setIsAudioEnabled(enabled);
    }
  }, [audioTrack]);

  React.useEffect(() => {
    if (!shouldInitialize) return;

    const init = async () => {
      setDevicesStatus("LOADING");

      const devices = await navigator.mediaDevices.enumerateDevices();
      setVideoDevices(devices.filter(d => d.kind === "videoinput"));
      setAudioDevices(devices.filter(d => d.kind === "audioinput"));

      const combinedStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      if (combinedStream.getVideoTracks().length === 0) {
        setDevicesStatus("ERROR");
        return;
      }
      if (combinedStream.getAudioTracks().length === 0) {
        setDevicesStatus("ERROR");
        return;
      }

      const videoTrack = combinedStream.getVideoTracks()[0];
      const audioTrack = combinedStream.getAudioTracks()[0];

      streamRef.current = combinedStream;
      setVideoTrack(videoTrack);
      setAudioTrack(audioTrack);
      setSelectedVideoDeviceId(videoTrack.getSettings().deviceId || "");
      setSelectedAudioDeviceId(audioTrack.getSettings().deviceId || "");
      setDevicesStatus("READY");
    };

    init();

    return () => {
      cleanupDevices();
    };
  }, [shouldInitialize, cleanupDevices]);

  return {
    videoDevices,
    audioDevices,
    videoTrack,
    audioTrack,
    selectedVideoDeviceId,
    selectedAudioDeviceId,
    isVideoEnabled,
    isAudioEnabled,
    devicesStatus,
    handleVideoChange,
    handleAudioChange,
    toggleVideo,
    toggleAudio,
    cleanupDevices,
    initializeDevices,
  };
}
